'use client';

import { Box, GridLayout, Typography } from '@wishket/design-system';
import { Suspense, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import PersonalizedContents from '../PersonalizedContents/PersonalizedContents';

import PersonalizedAreaRoleSelect from './PersonalizedAreaRoleSelect';
import PersonalizedAreaCareerSelect from './PersonalizedAreaCareerSelect';
import { useTrendingRandomCareerRole } from './models';
import PersonalizedAreaSkeleton from './parts/Skeleton';

import { PersonalizedAreaButton } from '@/features/Landing';
import { CareerItem, RoleItem } from '@/shared/types';
import { useDeviceCheck, useProfile } from '@/shared/hooks';
import { useProfileInfoQuery } from '@/entities/profile';

const PersonalizedArea = () => {
  const { profile } = useProfile();
  const yozmitProfileId = profile?.yozmitProfileId;
  const role = profile?.role;
  const roleKor = profile?.roleKor;
  const hasProfileProps = !!yozmitProfileId && !!role && !!roleKor;

  if (!hasProfileProps) return <PersonalizedAreaSkeleton />;

  return (
    <Suspense fallback={<PersonalizedAreaSkeleton />}>
      <PersonalizedAreaBody
        yozmitProfileId={yozmitProfileId}
        initialRoleKey={role}
        initialRoleValue={roleKor}
      />
    </Suspense>
  );
};

interface PersonalizedAreaBodyProps {
  yozmitProfileId: number;
  initialRoleKey: string;
  initialRoleValue: string;
}

const PersonalizedAreaBody = ({
  yozmitProfileId,
  initialRoleKey,
  initialRoleValue,
}: PersonalizedAreaBodyProps) => {
  const { isDesktop } = useDeviceCheck();
  const queryClient = useQueryClient();
  const { data: profileInfo } = useProfileInfoQuery({
    yozmitProfileId,
    useSuspense: true,
  });
  const careerSelectRef = useRef<HTMLDivElement>(null);

  const initialRole: RoleItem = {
    key: initialRoleKey,
    value: initialRoleValue,
  };
  const initialCareer: CareerItem = {
    key: profileInfo.career,
    value: `${profileInfo.career}년`,
  };

  const [career, setCareer] = useState<CareerItem>(initialCareer);
  const [role, setRole] = useState<RoleItem>(initialRole);
  const { mutate } = useTrendingRandomCareerRole();

  const moveToPersonalizedAreaTitle = () => {
    if (!isDesktop && careerSelectRef.current) {
      careerSelectRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const onPersonalizedButtonClick = () => {
    moveToPersonalizedAreaTitle();

    mutate(
      {
        year: Number(career.key),
        role: String(role.key),
      },
      {
        onSuccess: (data) => {
          setCareer({
            key: String(data.year),
            value: `${data.year}년`,
          });
          setRole({
            key: data.role,
            value: data.roleKor,
          });
          queryClient.refetchQueries({
            queryKey: ['advertisementsApi', 'landing_detail_3'],
          });
        },
      }
    );
  };

  return (
    <GridLayout gridTemplateAreaClass="gap-8 grid-areas-landing-personalized-mobile tablet:grid-areas-landing-personalized-tablet desktop:grid-areas-landing-personalized-desktop">
      <Box
        className="flex scroll-mt-[155px] flex-col gap-3 grid-in-select tablet:flex-row tablet:items-center"
        ref={careerSelectRef}
      >
        <PersonalizedAreaCareerSelect
          selectedCareer={career}
          handleCareerChange={setCareer}
        />
        <Box className="relative flex items-center gap-3">
          <PersonalizedAreaRoleSelect
            selectedRole={role}
            onRoleChange={setRole}
          />
          <Typography
            variant="subTitle1"
            className="text-center font-bold desktop:typo-title3"
          >
            가 많이 본
          </Typography>
        </Box>
      </Box>
      <Box className="grid-in-content">
        <PersonalizedContents
          year={Number(career.key)}
          role={String(role.key)}
        />
      </Box>
      <Box className="flex justify-center grid-in-button desktop:justify-end">
        <PersonalizedAreaButton onClick={onPersonalizedButtonClick} />
      </Box>
    </GridLayout>
  );
};
export default PersonalizedArea;
